import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { CandidateSessionConfiguration } from "src/app/models/models";
import { resolveSessionDetailRemark } from "src/app/cbta-web/ratings/container-ratings-form/shared-v2/utils/session-form.utils";
import {
	buildEts1SessionColumnTree,
	Ets1SessionColumnVM,
	Ets1SessionConfigItem,
	Ets1SessionTreeItem,
	flattenEts1SessionTree,
	hasEts1RadioChildren,
} from "./ets1-session-layout.utils";

export function resolveEts1SessionGroupCode(sessionKeys: any[] = []): string {
	return sessionKeys[0]?.name?.code ?? sessionKeys[0]?.code ?? "ts_1";
}

/** Collect every checked ETS1 session code from the flat ts_1 form group. */
export function collectEts1SelectedSessionCodes(
	sessionsGroup: UntypedFormGroup | null | undefined,
): string[] {
	if (!sessionsGroup) return [];
	return Object.entries(sessionsGroup.getRawValue() as Record<string, boolean>)
		.filter(([, selected]) => selected === true)
		.map(([code]) => code);
}

/**
 * Normalize draft/start-rating session payloads to flat session_conf codes.
 * Mirror: cbta-backend/src/form/ets1/flatten-candidate-session-details.utils.ts
 */
export function flattenCandidateSessionDetailCodes(sessionDetails: unknown): string[] {
	if (!sessionDetails) return [];
	if (Array.isArray(sessionDetails)) {
		if (!sessionDetails.length) return [];
		if (typeof sessionDetails[0] === "string") {
			return [...new Set(sessionDetails.map((code) => String(code).trim()).filter(Boolean))];
		}
		const codes: string[] = [];
		for (const detail of sessionDetails) {
			if (!detail || typeof detail !== "object") continue;
			for (const value of Object.values(detail as Record<string, unknown>)) {
				if (Array.isArray(value)) {
					codes.push(...value.map((code) => String(code).trim()).filter(Boolean));
					continue;
				}
				if (value && typeof value === "object") {
					codes.push(
						...Object.entries(value as Record<string, boolean>)
							.filter(([, selected]) => selected === true)
							.map(([code]) => code),
					);
				}
			}
		}
		return [...new Set(codes)];
	}
	return [];
}

/**
 * Drop a parent when a descendant is selected.
 * Keep a radio parent that has a remark and no selected child.
 * Backend filterEts1DisplaySessionCodes drops that parent as well.
 */
function resolveEts1SessionRemark(
	item: Ets1SessionTreeItem,
	remarksByCode: Record<string, string>,
	parentItem?: Ets1SessionTreeItem,
): string {
	const ownRemark = (remarksByCode[item.code] ?? "").trim();
	if (ownRemark) return ownRemark;
	if (parentItem && hasEts1RadioChildren(parentItem)) {
		return (remarksByCode[parentItem.code] ?? "").trim();
	}
	return "";
}

function collectSelectedEts1SessionConfigurations(
	columns: Ets1SessionColumnVM[],
	selectedCodes: Iterable<string>,
	remarksByCode: Record<string, string> = {},
): CandidateSessionConfiguration[] {
	const selected = new Set(selectedCodes);
	const configs: CandidateSessionConfiguration[] = [];

	const walk = (items: Ets1SessionTreeItem[], parentItem?: Ets1SessionTreeItem) => {
		for (const item of items) {
			const isSelected = selected.has(item.code);
			const selectedChildren = item.children.filter((child) => selected.has(child.code));

			if (item.children.length > 0) {
				if (selectedChildren.length > 0) {
					walk(selectedChildren, item);
					continue;
				}
				if (!isSelected) continue;
				if (hasEts1RadioChildren(item) && !resolveEts1SessionRemark(item, remarksByCode, parentItem)) continue;
			} else if (!isSelected) {
				continue;
			}

			const remark = resolveEts1SessionRemark(item, remarksByCode, parentItem);
			configs.push({
				code: item.code,
				description: item.description,
				...(remark ? { selectionRemarks: remark } : {}),
			});
		}
	};

	for (const column of columns) {
		walk(column.items);
	}

	return configs;
}

/** Admin form / template save: resolve flat session controls via the session tree. */
export function extractEts1SessionConfigurationsFromColumns(
	columns: Ets1SessionColumnVM[],
	sessions: Record<string, boolean>,
	sessionRemarks: Record<string, string> = {},
): CandidateSessionConfiguration[] {
	const selectedCodes = Object.entries(sessions ?? {})
		.filter(([, selected]) => selected === true)
		.map(([code]) => code);

	return collectSelectedEts1SessionConfigurations(columns, selectedCodes, sessionRemarks ?? {});
}

/** Build selected-session labels from catalog + persisted codes (report/checklist). */
export function extractEts1SessionConfigurationsFromSelection(
	catalog: Ets1SessionConfigItem[],
	selectedCodes: string[],
	remarksByCode: Record<string, string> = {},
): CandidateSessionConfiguration[] {
	return collectSelectedEts1SessionConfigurations(
		buildEts1SessionColumnTree(catalog),
		selectedCodes,
		remarksByCode,
	);
}

/** ETS1 v2 AOC sessions store flat boolean controls under one group code. */
export function extractEts1SessionConfigurationsFromCandidate(
	candidateControl: UntypedFormGroup,
	columns: Ets1SessionColumnVM[],
	groupCode: string,
): CandidateSessionConfiguration[] {
	const sessionsGroup = candidateControl.get("sessionDetails")?.get(groupCode) as UntypedFormGroup | null;
	const remarksGroup = candidateControl.get("sessionRemarks")?.get(groupCode) as UntypedFormGroup | null;
	if (!sessionsGroup || !columns.length) return [];

	const remarksRaw = (remarksGroup?.getRawValue() ?? {}) as Record<string, unknown>;
	const selectedCodes: string[] = [];
	const remarksByCode: Record<string, string> = {};

	for (const item of flattenEts1SessionTree(columns)) {
		if (sessionsGroup.get(item.code)?.value === true) selectedCodes.push(item.code);
		const remark = resolveSessionDetailRemark(remarksRaw, item.code);
		if (remark) remarksByCode[item.code] = remark;
	}

	return collectSelectedEts1SessionConfigurations(columns, selectedCodes, remarksByCode);
}

/** Build read-only ETS1 session controls for report/checklist preview. */
export function buildEts1SessionReadOnlyFormGroups(
	fb: UntypedFormBuilder,
	catalog: Ets1SessionConfigItem[],
	selectedCodes: string[],
	remarksByCode: Record<string, string> = {},
) {
	const columns = buildEts1SessionColumnTree(catalog);
	const selected = new Set(selectedCodes);
	const sessionControls: Record<string, UntypedFormControl> = {};
	const remarkControls: Record<string, UntypedFormControl> = {};

	for (const item of flattenEts1SessionTree(columns)) {
		sessionControls[item.code] = fb.control({ value: selected.has(item.code), disabled: true });
		remarkControls[item.code] = fb.control({
			value: remarksByCode[item.code] ?? "",
			disabled: true,
		});
	}

	return {
		columns,
		sessions: fb.group(sessionControls),
		sessionRemarks: fb.group(remarkControls),
	};
}
